import { supabase } from '../lib/supabaseClient';
import type { ProjectEmail, EmailThread, EmailRateLimits, EmailAttachment } from '../types/email';

const API_BASE = import.meta.env.VITE_API_URL || 'https://joules-admin-backend-580766458255.us-central1.run.app';

/**
 * Get auth headers with current user session token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();

  return {
    'Content-Type': 'application/json',
    ...(session?.access_token && { 'Authorization': `Bearer ${session.access_token}` })
  };
}

export const emailService = {
  async sendEmail(data: {
    project_id: string;
    to: string;
    subject: string;
    body_html: string;
    body_text: string;
    in_reply_to?: string;
    references?: string;
    attachment_ids?: string[];
  }) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/v1/project-email/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to send email');
    return response.json();
  },

  /**
   * Upload attachment to storage and create database record
   * Returns attachment ID to be passed to sendEmail
   */
  async uploadAttachment(file: File, projectId: string): Promise<string> {
    try {
      // Generate unique storage path
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Get tenant_id from project (simpler and more reliable)
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('tenant_id')
        .eq('id', projectId)
        .single();

      if (projectError || !project?.tenant_id) {
        throw new Error('Failed to get tenant information from project');
      }

      const tenantId = project.tenant_id;
      const timestamp = Date.now();
      const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      // Storage path: tenant_id/project_id/temp_uploads/timestamp-filename
      const storagePath = `${tenantId}/${projectId}/temp_uploads/${timestamp}-${sanitizedFilename}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('email-attachments')
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Create database record (without email_id - backend will link it after sending)
      const { data: dbRecord, error: dbError } = await supabase
        .from('project_email_attachments')
        .insert({
          email_id: null, // Will be set by backend after email is created
          project_id: projectId,
          filename: file.name,
          content_type: file.type,
          file_size: file.size,
          storage_path: storagePath,
          scan_status: 'pending'
        })
        .select('id')
        .single();

      if (dbError) {
        // If database insert fails, try to clean up uploaded file
        await supabase.storage.from('email-attachments').remove([storagePath]);
        throw new Error(`Failed to save attachment: ${dbError.message}`);
      }

      return dbRecord.id;
    } catch (error) {
      throw error;
    }
  },

  async getRateLimits(projectId: string): Promise<EmailRateLimits> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/api/v1/project-email/limits?project_id=${projectId}`,
      { method: 'GET', headers }
    );
    if (!response.ok) throw new Error('Failed to fetch rate limits');
    return response.json();
  },

  async getThreads(projectId: string): Promise<EmailThread[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/api/v1/project-email/threads/${projectId}`,
      { method: 'GET', headers }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch email threads');
    }
    const data = await response.json();
    return data.threads || [];
  },

  /**
   * Get attachments for a specific email
   */
  async getAttachments(emailId: string): Promise<EmailAttachment[]> {
    const { data, error } = await supabase
      .from('project_email_attachments')
      .select('*')
      .eq('email_id', emailId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Generate signed download URL for attachment (1-hour expiration)
   * Returns null if file doesn't exist (common for old/cleaned up attachments)
   */
  async getAttachmentDownloadUrl(storagePath: string): Promise<string | null> {
    try {
      // First check if file exists by listing the path
      // Extract folder and filename from path
      const lastSlash = storagePath.lastIndexOf('/');
      const folder = lastSlash > 0 ? storagePath.substring(0, lastSlash) : '';
      const filename = lastSlash > 0 ? storagePath.substring(lastSlash + 1) : storagePath;

      const { data: files } = await supabase.storage
        .from('email-attachments')
        .list(folder, { limit: 1, search: filename });

      // If file doesn't exist, return null without making signed URL request
      if (!files || files.length === 0) {
        return null;
      }

      const { data, error } = await supabase.storage
        .from('email-attachments')
        .createSignedUrl(storagePath, 3600); // 1 hour = 3600 seconds

      if (error || !data?.signedUrl) {
        return null;
      }

      return data.signedUrl;
    } catch {
      // Storage error - file likely doesn't exist
      return null;
    }
  },

  /**
   * Download attachment directly (triggers browser download)
   */
  async downloadAttachment(attachment: EmailAttachment): Promise<void> {
    const signedUrl = await this.getAttachmentDownloadUrl(attachment.storage_path);

    if (!signedUrl) {
      throw new Error('File not found or no longer available');
    }

    // Create temporary link and trigger download
    const link = document.createElement('a');
    link.href = signedUrl;
    link.download = attachment.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  /**
   * Send notification email (non-project-specific)
   */
  async sendNotificationEmail(params: {
    to: string;
    subject: string;
    message: string;
    type: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/v1/email/send-notification-email`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params)
    });
    if (!response.ok) throw new Error('Failed to send notification email');
  }
};
